import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Spin,
  Switch,
  Segmented,
  TimePicker,
  Tooltip,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  PrinterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';

const { Panel } = Collapse;
const { Text } = Typography;

const OWNERSHIP_OPTIONS = [
  { value: 9,   label: 'Акционерное общество' },
  { value: 61,  label: 'Закрытое акционерное общество' },
  { value: 63,  label: 'Индивидуальный предприниматель' },
  { value: 119, label: 'Открытое акционерное общество' },
  { value: 137, label: 'Общество с ограниченной ответственностью' },
  { value: 147, label: 'Публичное акционерное общество' },
  { value: 179, label: 'Товарищество с ограниченной ответственностью' },
];

const calcDisplayWeight = (items = []) =>
  items.reduce((sum, item) => sum + (item?.weight || 0) * (item?.amount || 1), 0);

const CdekOrderForm = ({
  form,
  cdekOrder,
  orderType, setOrderType,
  pickupMode, setPickupMode,
  deliveryMode, setDeliveryMode,
  availableTariffs,
  selectedTariffCode, setSelectedTariffCode,
  calculatedPrice, setCalculatedPrice,
  detailedPrice,
  loadingTariffs, loadingCreate, loadingDetailed, loadingTemplate,
  loadingOrderInfo, loadingWaybillInfo, loadingWaybillDownload, loadingBarcodeInfo, loadingBarcodeDownload,
  senderCityOptions, recipientCityOptions,
  loadingSenderCity, loadingRecipientCity,
  selectedSenderCity, setSelectedSenderCity,
  selectedRecipientCity, setSelectedRecipientCity,
  setSenderSearch, setRecipientSearch,
  pickupAddressOptions, deliveryAddressOptions,
  loadingPickupAddress, loadingDeliveryAddress,
  setPickupAddressSearch, setDeliveryAddressSearch,
  pickupPvzOptions, deliveryPvzOptions,
  loadingPickupPvz, loadingDeliveryPvz,
  selectedPickupPvz, setSelectedPickupPvz,
  selectedDeliveryPoint, setSelectedDeliveryPoint,
  waybillUuid, barcodeUuid, waybillInfo, barcodeInfo,
  loadTemplate,
  handleCalculateAvailable,
  handleCreateOrder, handleDeleteOrder, handleGetOrderInfo,
  printWaybill, printBarcode,
  handleGetWaybillInfo, handleDownloadWaybill,
  handleGetBarcodeInfo, handleDownloadBarcode,
}) => {
  const [showExtra, setShowExtra] = useState(false);

  return (
    <>
      <div style={{ display: 'none' }}>
        {[
          'sender_city_data', 'recipient_city_data', 'pickup_address_data',
          'delivery_address_data', 'pickup_pvz_data', 'delivery_point_data',
          'pickup_date', 'pickup_time_from', 'pickup_time_to',
          'pickup_break_from', 'pickup_break_to',
          'pickup_need_authority', 'pickup_need_passport',
        ].map((n) => (
          <Form.Item key={n} name={n} />
        ))}
      </div>

      {!cdekOrder && (
        <>
          {loadingTariffs && (
            <Alert
              type="info"
              showIcon
              icon={<Spin size="small" />}
              message="Рассчитываем доступные тарифы..."
              style={{ marginBottom: 12 }}
            />
          )}
          {loadingDetailed && (
            <Alert
              type="info"
              showIcon
              icon={<Spin size="small" />}
              message="Считаем стоимость..."
              style={{ marginBottom: 12 }}
            />
          )}

          <Space style={{ width: '100%', marginBottom: 16 }}>
            <Tooltip title="Перезагрузить шаблон">
              <Button
                icon={<ReloadOutlined />}
                onClick={loadTemplate}
                loading={loadingTemplate}
              />
            </Tooltip>
            <Tooltip title={showExtra ? 'Скрыть доп. параметры' : 'Доп. параметры'}>
              <Button
                icon={<SettingOutlined />}
                type={showExtra ? 'primary' : 'default'}
                onClick={() => setShowExtra((v) => !v)}
              />
            </Tooltip>
          </Space>

          {showExtra && (
            <Card title="Тип заказа" size="small" style={{ marginBottom: 16 }}>
              <Radio.Group value={orderType} onChange={(e) => setOrderType(e.target.value)} block>
                <Radio.Button value="im">Интернет-магазин</Radio.Button>
                <Radio.Button value="delivery">Доставка</Radio.Button>
              </Radio.Group>
            </Card>
          )}

          {showExtra && (
          <Card title="Отправитель (вы)" size="small" style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Form.Item label="Наименование компании" name="sender_company" required>
                <Input />
              </Form.Item>
              <Form.Item label="ФИО" name="sender_fio" required>
                <Input />
              </Form.Item>
              <Form.Item label="Телефон" name="sender_phone" required>
                <Input placeholder="+7 (___) ___-__-__" />
              </Form.Item>
              <Form.Item label="Город отправителя" required>
                <Select
                  showSearch allowClear
                  placeholder="Введите город"
                  options={senderCityOptions}
                  loading={loadingSenderCity}
                  onSearch={setSenderSearch}
                  filterOption={false}
                  value={selectedSenderCity?.full_name || undefined}
                  onSelect={(_, option) => {
                    setSelectedSenderCity(option.data);
                    form.setFieldsValue({
                      sender_city: option.data.full_name,
                      sender_city_data: option.data,
                    });
                  }}
                  onClear={() => {
                    setSelectedSenderCity(null);
                    form.setFieldsValue({ sender_city: '', sender_city_data: null });
                  }}
                />
              </Form.Item>
              {showExtra && (
                <Form.Item label="ИНН юр. лица" name="sender_inn">
                  <Input />
                </Form.Item>
              )}
            </Space>
          </Card>
          )}
          {orderType === 'im' && showExtra && (
            <Card title="Истинный продавец" size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Form.Item label="Номер отправления ИМ" name="im_number"><Input /></Form.Item>
                <Form.Item label="ФИО истинного продавца" name="seller_name"><Input /></Form.Item>
                <Form.Item label="Форма собственности" name="seller_ownership_form">
                  <Select options={OWNERSHIP_OPTIONS} />
                </Form.Item>
                <Form.Item label="ИНН" name="seller_inn"><Input /></Form.Item>
                <Form.Item label="Телефон" name="seller_phone"><Input /></Form.Item>
              </Space>
            </Card>
          )}

          <Card title="Получатель" size="small" style={{ marginBottom: 16 }}>
            <Form.Item label="ФИО" name="recipient_fio" required>
              <Input />
            </Form.Item>
            <Form.Item label="Телефон" name="recipient_phone" required>
              <Input />
            </Form.Item>
            <Form.Item label="Город получателя" required>
              <Select
                showSearch allowClear
                placeholder="Введите город"
                options={recipientCityOptions}
                loading={loadingRecipientCity}
                onSearch={setRecipientSearch}
                filterOption={false}
                value={selectedRecipientCity?.full_name || undefined}
                onSelect={(_, option) => {
                  setSelectedRecipientCity(option.data);
                  form.setFieldsValue({
                    recipient_city: option.data.full_name,
                    recipient_city_data: option.data,
                  });
                }}
                onClear={() => {
                  setSelectedRecipientCity(null);
                  form.setFieldsValue({ recipient_city: '', recipient_city_data: null });
                }}
              />
            </Form.Item>
            {showExtra && (
              <Form.Item label="Email" name="recipient_email">
                <Input />
              </Form.Item>
            )}
          </Card>

          {showExtra && (
            <Card title="Товары (коробки)" size="small" style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                Габариты коробки фиксированы: 23×19×10 см. Вес = Σ(вес × кол-во).
              </Text>
              <Form.List name="packages">
                {(fields, { add, remove }) => (
                  <>
                    <Collapse>
                      {fields.map((field, idx) => {
                        const pkgItems = form.getFieldValue(['packages', field.name, 'items']) || [];
                        const displayWeight = calcDisplayWeight(pkgItems);
                        return (
                          <Panel
                            key={field.key}
                            header={
                              <span>
                                Коробка {idx + 1}
                                {displayWeight > 0 && (
                                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                                    ~{(displayWeight / 1000).toFixed(2)} кг
                                  </Text>
                                )}
                              </span>
                            }
                          >
                            <Space direction="vertical" style={{ width: '100%' }}>
                              <Form.List name={[field.name, 'items']}>
                                {(itemFields, { add: addItem, remove: removeItem }) => (
                                  <>
                                    {itemFields.map((itemField) => (
                                      <Card key={itemField.key} size="small" style={{ marginBottom: 8 }}>
                                        <Form.Item
                                          label="Наименование"
                                          name={[itemField.name, 'name']}
                                          rules={[{ required: true }]}
                                        >
                                          <Input />
                                        </Form.Item>
                                        <Space>
                                          <Form.Item
                                            label="Артикул"
                                            name={[itemField.name, 'ware_key']}
                                            required
                                          >
                                            <Input />
                                          </Form.Item>
                                        </Space>
                                        <Space>
                                          <Form.Item
                                            label="Вес ед. (г)"
                                            name={[itemField.name, 'weight']}
                                            required
                                          >
                                            <InputNumber min={1} />
                                          </Form.Item>
                                          <Form.Item
                                            label="Кол-во"
                                            name={[itemField.name, 'amount']}
                                            required
                                          >
                                            <InputNumber min={1} />
                                          </Form.Item>
                                          <Form.Item
                                            label="Стоимость ₽"
                                            name={[itemField.name, 'cost']}
                                            required
                                          >
                                            <InputNumber min={0} />
                                          </Form.Item>
                                        </Space>
                                        <Button
                                          danger
                                          icon={<DeleteOutlined />}
                                          onClick={() => removeItem(itemField.name)}
                                          style={{ marginTop: 4 }}
                                        >
                                          Удалить товар
                                        </Button>
                                      </Card>
                                    ))}
                                    <Button
                                      type="dashed"
                                      onClick={() => addItem({
                                        name: '',
                                        ware_key: '',
                                        weight: 1000,
                                        amount: 1,
                                        cost: 0,
                                      })}
                                      block
                                      icon={<PlusOutlined />}
                                    >
                                      + Товар
                                    </Button>
                                  </>
                                )}
                              </Form.List>
                              <Button
                                danger
                                onClick={() => remove(field.name)}
                                block
                                style={{ marginTop: 8 }}
                              >
                                Удалить коробку
                              </Button>
                            </Space>
                          </Panel>
                        );
                      })}
                    </Collapse>
                    <Button
                      type="dashed"
                      onClick={() => add({ is_dangerous: false, items: [] })}
                      block
                      icon={<PlusOutlined />}
                      style={{ marginTop: 12 }}
                    >
                      Добавить коробку
                    </Button>
                  </>
                )}
              </Form.List>
            </Card>
          )}


          <Card title="Способ забора и доставки" size="small" style={{ marginBottom: 16 }}>
            <Form.Item label="Забор груза" required>
              <Segmented
                options={[
                  { label: 'Курьером по адресу', value: 'door' },
                  { label: 'Самостоятельно в ПВЗ', value: 'pvz' },
                ]}
                value={pickupMode}
                onChange={(val) => {
                  setPickupMode(val);
                  form.setFieldsValue({ pickup_mode: val });
                }}
                block
              />
            </Form.Item>
            <Form.Item label="Доставка получателю" required>
              <Segmented
                options={[
                  { label: 'Курьером', value: 'door' },
                  { label: 'В ПВЗ', value: 'pvz' },
                  { label: 'В постамат', value: 'postamat' },
                ]}
                value={deliveryMode}
                onChange={(val) => {
                  setDeliveryMode(val);
                  form.setFieldsValue({ delivery_mode: val });
                }}
                block
              />
            </Form.Item>
          </Card>

          {pickupMode === 'door' && (
            <Card title="Адрес забора курьером" size="small" style={{ marginBottom: 16 }}>
              <Form.Item
                name="pickup_address"
                required
                rules={[{ required: true, message: 'Выберите адрес' }]}
              >
                <Select
                  showSearch allowClear
                  placeholder={selectedSenderCity ? 'Введите адрес' : 'Недоступно — выберите город отправителя'}
                  disabled={!selectedSenderCity}
                  options={pickupAddressOptions}
                  loading={loadingPickupAddress}
                  onSearch={setPickupAddressSearch}
                  filterOption={false}
                  notFoundContent={loadingPickupAddress ? <Spin size="small" /> : null}
                  onSelect={(value, option) =>
                    form.setFieldsValue({ pickup_address: value, pickup_address_data: option.data })
                  }
                />
              </Form.Item>
              {showExtra && (
                <>
                  <Form.Item label="Комментарий для курьера" name="pickup_comment">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                  <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'baseline' }}>
                    <Form.Item label="Дата забора" name="pickup_date" style={{ marginBottom: 0 }}>
                      <DatePicker placeholder="дд.мм.гггг" />
                    </Form.Item>
                    <Form.Item label="От" name="pickup_time_from">
                      <TimePicker placeholder="09:00" format="HH:mm" />
                    </Form.Item>
                    <Form.Item label="До" name="pickup_time_to">
                      <TimePicker placeholder="18:00" format="HH:mm" />
                    </Form.Item>
                  </Space>
                  <Divider style={{ margin: '12px 0' }} />
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space>
                      <span>Перерыв:</span>
                      <Form.Item name="pickup_break_from" noStyle>
                        <TimePicker placeholder="--:--" format="HH:mm" />
                      </Form.Item>
                      <span>–</span>
                      <Form.Item name="pickup_break_to" noStyle>
                        <TimePicker placeholder="--:--" format="HH:mm" />
                      </Form.Item>
                    </Space>
                    <Form.Item name="pickup_need_authority" valuePropName="checked" style={{ marginBottom: 8 }}>
                      <Switch /> <span style={{ marginLeft: 8 }}>Курьеру необходима доверенность</span>
                    </Form.Item>
                    <Form.Item name="pickup_need_passport" valuePropName="checked">
                      <Switch /> <span style={{ marginLeft: 8 }}>Курьеру необходим документ</span>
                    </Form.Item>
                  </Space>
                </>
              )}
            </Card>
          )}
          {pickupMode === 'pvz' && (
            <Card title="ПВЗ отправления" size="small" style={{ marginBottom: 16 }}>
              <Form.Item required>
                <Select
                  placeholder={selectedSenderCity ? 'Выберите ПВЗ' : 'Недоступно — выберите город отправителя'}
                  disabled={!selectedSenderCity}
                  options={pickupPvzOptions}
                  loading={loadingPickupPvz}
                  showSearch optionFilterProp="label"
                  value={selectedPickupPvz?.location?.address_full || undefined}
                  onSelect={(_, option) => {
                    setSelectedPickupPvz(option.data);
                    form.setFieldsValue({ pickup_pvz_data: option.data });
                  }}
                />
              </Form.Item>
            </Card>
          )}

          {deliveryMode === 'door' && (
            <Card title="Адрес доставки курьером" size="small" style={{ marginBottom: 16 }}>
              <Form.Item
                name="delivery_address"
                required
                rules={[{ required: true, message: 'Выберите адрес' }]}
              >
                <Select
                  showSearch allowClear
                  placeholder={selectedRecipientCity ? 'Введите адрес' : 'Недоступно — выберите город получателя'}
                  disabled={!selectedRecipientCity}
                  options={deliveryAddressOptions}
                  loading={loadingDeliveryAddress}
                  onSearch={setDeliveryAddressSearch}
                  filterOption={false}
                  notFoundContent={loadingDeliveryAddress ? <Spin size="small" /> : null}
                  onSelect={(value, option) =>
                    form.setFieldsValue({ delivery_address: value, delivery_address_data: option.data })
                  }
                />
              </Form.Item>
              {showExtra && (
                <Form.Item label="Комментарий для курьера" name="delivery_comment">
                  <Input.TextArea rows={2} />
                </Form.Item>
              )}
            </Card>
          )}

          {(deliveryMode === 'pvz' || deliveryMode === 'postamat') && (
            <Card
              title={deliveryMode === 'pvz' ? 'ПВЗ получения' : 'Постамат получения'}
              size="small"
              style={{ marginBottom: 16 }}
            >
              <Form.Item required>
                <Select
                  placeholder={selectedRecipientCity ? `Выберите ${deliveryMode === 'pvz' ? 'ПВЗ' : 'постамат'}` : 'Недоступно — выберите город получателя'}
                  disabled={!selectedRecipientCity}
                  options={deliveryPvzOptions}
                  loading={loadingDeliveryPvz}
                  showSearch optionFilterProp="label"
                  value={selectedDeliveryPoint?.location?.address_full || undefined}
                  onSelect={(_, option) => {
                    setSelectedDeliveryPoint(option.data);
                    form.setFieldsValue({ delivery_point_data: option.data });
                  }}
                />
              </Form.Item>
            </Card>
          )}

          <Card title="Тарифы" size="small" style={{ marginBottom: 16 }}>
            {loadingTariffs && (
              <div style={{ textAlign: 'center', padding: '12px 0' }}>
                <Spin tip="Рассчитываем тарифы..." />
              </div>
            )}
            {!loadingTariffs && availableTariffs.length === 0 && selectedSenderCity && selectedRecipientCity && (
              <Alert type="warning" showIcon message="Тарифы не найдены. Попробуйте изменить параметры." style={{ marginBottom: 8 }} />
            )}
            {!loadingTariffs && (!selectedSenderCity || !selectedRecipientCity) && (
              <Alert type="info" showIcon message="Укажите города отправителя и получателя для расчёта тарифов." />
            )}

            {availableTariffs.length > 0 && (
              <Form.Item label="Выберите тариф" name="tariff_code" rules={[{ required: true }]} style={{ marginTop: 8 }}>
                <Radio.Group
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedTariffCode(val);
                    setCalculatedPrice(availableTariffs.find((t) => t.tariff_code === val));
                  }}
                  value={selectedTariffCode}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {availableTariffs.map((t) => (
                      <Radio.Button key={t.tariff_code} value={t.tariff_code} style={{ width: '100%', textAlign: 'left' }}>
                        {t.tariff_name} — {t.delivery_sum} ₽ ({t.period_min}–{t.period_max} дн.)
                      </Radio.Button>
                    ))}
                  </Space>
                </Radio.Group>
              </Form.Item>
            )}

            {calculatedPrice && (
              <Alert
                message={`${calculatedPrice.delivery_sum} ₽ | ${calculatedPrice.period_min}–${calculatedPrice.period_max} дней`}
                type="success"
                showIcon
                style={{ marginTop: 8 }}
              />
            )}
          </Card>
          {loadingDetailed && (
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <Spin tip="Считаем стоимость..." />
            </div>
          )}
          {detailedPrice && !loadingDetailed && (
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', borderBottom: '1px dashed #ccc', paddingBottom: 8, marginBottom: 8 }}>
                <span style={{ fontWeight: 'bold' }}>{selectedSenderCity?.full_name || 'Город отправителя'}</span>
                <span>↓</span>
                <span style={{ fontWeight: 'bold' }}>{selectedRecipientCity?.full_name || 'Город получателя'}</span>
              </div>
              {(() => {
                const pkgs = form.getFieldsValue(true).packages || [];
                const totalG = pkgs.reduce(
                  (s, p) => s + ((p.items || []).reduce((si, i) => si + (i?.weight || 0) * (i?.amount || 1), 0)),
                  0
                );
                return totalG > 0 ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Вес к оплате</span>
                    <span>{(totalG / 1000).toFixed(3)} кг</span>
                  </div>
                ) : null;
              })()}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '1.2em', marginBottom: 4 }}>
                <span>Итого</span>
                <span>{detailedPrice.total_sum?.toFixed(2).replace('.', ',')} ₽</span>
              </div>
              <div style={{ fontSize: '0.85em', color: '#888', marginBottom: 8 }}>С учётом НДС и скидки</div>
              <div style={{ borderTop: '1px solid #eee', paddingTop: 8 }}>
                {[['Доставка', detailedPrice.delivery_sum], ['Скидка', 0], ['НДС', 0]].map(([label, val]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: '#666' }}>{label}</span>
                    <span>{(val || 0).toFixed(2).replace('.', ',')} ₽</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Button
            type="primary"
            onClick={handleCreateOrder}
            loading={loadingCreate}
            disabled={!selectedTariffCode}
            block
            size="large"
            style={{ marginTop: 8 }}
          >
            Создать заказ в СДЭК
          </Button>
        </>
      )}

      {cdekOrder && (
        <Card size="small" title="Заказ создан" style={{ marginTop: 16 }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="UUID">{cdekOrder?.entity?.uuid}</Descriptions.Item>
            <Descriptions.Item label="Статус">
              {cdekOrder.requests?.[0]?.state || cdekOrder.entity?.status || 'Неизвестно'}
            </Descriptions.Item>
          </Descriptions>
          <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
            <Button icon={<PrinterOutlined />} onClick={() => printWaybill(cdekOrder.entity?.uuid)} block>
              Сформировать накладную
            </Button>
            <Button icon={<PrinterOutlined />} onClick={() => printBarcode(cdekOrder.entity?.uuid)} block>
              Сформировать штрихкод
            </Button>
            <Button type="primary" onClick={handleGetOrderInfo} loading={loadingOrderInfo} block>
              Получить актуальную информацию
            </Button>
            {waybillUuid && (
              <>
                <Button onClick={handleGetWaybillInfo} loading={loadingWaybillInfo} block>
                  Информация о квитанции
                </Button>
                <Button onClick={handleDownloadWaybill} loading={loadingWaybillDownload} block>
                  Скачать квитанцию
                </Button>
              </>
            )}
            {barcodeUuid && (
              <>
                <Button onClick={handleGetBarcodeInfo} loading={loadingBarcodeInfo} block>
                  Информация о ШК
                </Button>
                <Button onClick={handleDownloadBarcode} loading={loadingBarcodeDownload} block>
                  Скачать ШК
                </Button>
              </>
            )}
            <Button danger onClick={handleDeleteOrder} block>
              Удалить заказ
            </Button>
          </Space>
          {waybillInfo && (
            <Alert
              message="Информация о квитанции"
              description={<pre>{JSON.stringify(waybillInfo, null, 2)}</pre>}
              type="info"
              style={{ marginTop: 8 }}
            />
          )}
          {barcodeInfo && (
            <Alert
              message="Информация о ШК"
              description={<pre>{JSON.stringify(barcodeInfo, null, 2)}</pre>}
              type="info"
              style={{ marginTop: 8 }}
            />
          )}
        </Card>
      )}
    </>
  );
};

export default CdekOrderForm;